import { For, Show, createMemo, createResource, createSignal } from 'solid-js';
import { useApi, isOk } from '../api';
import type { InventoryCustomItemDto, ItemStackDto } from '../api';
import { firstError } from '../api/envelope';
import Text from '../components/Text';
import ItemStackView from '../components/ItemStackView';
import MinecraftText from '../components/MinecraftText';
import { showItemInfo } from '../components/ItemInfoView';
import { Badge } from '../components/Badge';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { itemKey } from '../services/craftingResolver';
import { resolveItemKeyDisplayName, stripColorCodes } from '../services/itemDisplayName';
import { fromItemStackDto } from '../services/itemRefAdapters';
import { cachedNameOf, createSellbotNameCache, fromCustomItem, matchItemsByRelevance } from '../services/sellbotItemSearch';
import { formatInteger } from '../services/numberFormat';

const PAGE_SIZE = 30;

const productKeyOf = (entry: InventoryCustomItemDto) => itemKey(entry.itemId, entry.metadata, entry.productHash);
const loreOf = (entry: InventoryCustomItemDto) => stripColorCodes(entry.lore ?? '');

export default function AdminCustomItems() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();

    const [search, setSearch] = createSignal('');
    const [page, setPage] = createSignal(0);

    const [registry] = createResource(async (): Promise<InventoryCustomItemDto[]> => {
        try {
            const res = await api.inventory.getCustomItems();
            if (isOk(res.data)) return res.data.value.items ?? [];
            toast?.error(firstError(res.data) ?? t('CUSTOM_ITEMS_LOAD_FAILED'));
        } catch (e) {
            toast?.error((e as Error)?.message ?? t('CUSTOM_ITEMS_LOAD_FAILED'));
        }
        return [];
    });

    const entryByKey = createMemo(() => new Map((registry() ?? []).map(entry => [fromCustomItem(entry).key, entry])));

    const plainName = (entry: InventoryCustomItemDto) => entry.displayName
        ? stripColorCodes(entry.displayName)
        : resolveItemKeyDisplayName(itemKey(entry.itemId, entry.metadata), null, resolver());

    // Rebuilt once the resolver loads, so vanilla names cached before it arrived do not stick.
    const searchNameOf = createMemo(() => {
        resolver();
        return cachedNameOf(createSellbotNameCache(), (stack: ItemStackDto) => plainName(entryByKey().get(stack.key)!));
    });

    const stacksByName = createMemo(() => {
        const nameOf = searchNameOf();
        return (registry() ?? []).map(fromCustomItem).sort((left, right) => nameOf(left).localeCompare(nameOf(right)));
    });

    const copiesByProduct = createMemo(() => {
        const counts = new Map<string, number>();
        for (const entry of registry() ?? []) counts.set(productKeyOf(entry), (counts.get(productKeyOf(entry)) ?? 0) + 1);
        return counts;
    });

    const matchingStacks = createMemo(() => {
        const query = search().trim().toLowerCase();
        const byName = matchItemsByRelevance(query, stacksByName(), searchNameOf());
        if (!query) return byName;
        const matchedKeys = new Set(byName.map(stack => stack.key));
        const byLore = stacksByName().filter(stack =>
            !matchedKeys.has(stack.key) && loreOf(entryByKey().get(stack.key)!).toLowerCase().includes(query));
        return [...byName, ...byLore];
    });

    const totalPages = createMemo(() => Math.max(1, Math.ceil(matchingStacks().length / PAGE_SIZE)));
    const pageStacks = createMemo(() => {
        const start = Math.min(page(), totalPages() - 1) * PAGE_SIZE;
        return matchingStacks().slice(start, start + PAGE_SIZE);
    });

    const openDetails = (stack: ItemStackDto) => {
        const definition = resolver()?.getItemById(stack.itemId, stack.metadata) ?? resolver()?.getItemById(stack.itemId, 0);
        showItemInfo({
            itemId: stack.itemId,
            count: 1,
            damage: stack.metadata,
            identifier: definition ? `minecraft:${definition.name}` : undefined,
            name: definition?.name,
            displayName: plainName(entryByKey().get(stack.key)!),
            snbt: stack.snbt ?? undefined,
        });
    };

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="CUSTOM_ITEMS_TITLE" /></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    <Text val="CUSTOM_ITEMS_SUBTITLE" />
                </span>
            </div>

            <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <div style={{ flex: '1 1 14rem' }}>
                    <SearchInput
                        value={search()}
                        onInput={(next) => { setSearch(next); setPage(0); }}
                        placeholder={t('CUSTOM_ITEMS_SEARCH_PLACEHOLDER')}
                        ariaLabel={t('CUSTOM_ITEMS_SEARCH_PLACEHOLDER')}
                    />
                </div>
                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                    {t('CUSTOM_ITEMS_COUNT', formatInteger(matchingStacks().length))}
                </span>
            </div>

            <Show
                when={registry.state !== 'pending'}
                fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonAuditRow /><SkeletonAuditRow /><SkeletonAuditRow /></div>}
            >
                <Show
                    when={matchingStacks().length > 0}
                    fallback={<EmptyState
                        icon="fa-tags"
                        title={t(search().trim() ? 'CUSTOM_ITEMS_EMPTY_FILTERED' : 'CUSTOM_ITEMS_EMPTY')}
                        hint={search().trim() ? undefined : t('CUSTOM_ITEMS_EMPTY_HINT')}
                    />}
                >
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <For each={pageStacks()}>{(stack) => {
                            const entry = () => entryByKey().get(stack.key)!;
                            const copies = () => copiesByProduct().get(productKeyOf(entry())) ?? 1;
                            return (
                                <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)', 'flex-wrap': 'wrap' }}>
                                    <ItemStackView stack={fromItemStackDto(stack)} size={40} showTooltip />
                                    <div class="v" style={{ flex: '1 1 14rem', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                        <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center', 'flex-wrap': 'wrap' }}>
                                            <span style={{ 'font-weight': 'var(--font-weight-semibold)' }}>
                                                <Show when={entry().displayName} fallback={plainName(entry())}>
                                                    {(displayName) => <MinecraftText text={displayName()} />}
                                                </Show>
                                            </span>
                                            <Show when={copies() > 1}>
                                                <Badge tone="info">{t('CUSTOM_ITEMS_COPIES', formatInteger(copies()))}</Badge>
                                            </Show>
                                        </div>
                                        <Show when={loreOf(entry())}>
                                            <div style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-secondary)', overflow: 'hidden', 'text-overflow': 'ellipsis', 'white-space': 'nowrap' }}>
                                                {loreOf(entry()).split('\n').join(' · ')}
                                            </div>
                                        </Show>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)', 'overflow-wrap': 'anywhere' }}>
                                            {itemKey(stack.itemId, stack.metadata)} · {stack.nbtHash}
                                        </div>
                                    </div>
                                    <button class="btn btn-secondary btn-sm" onClick={() => openDetails(stack)}>
                                        <i class="fa-solid fa-magnifying-glass" /> <Text val="CUSTOM_ITEMS_DETAILS" />
                                    </button>
                                </div>
                            );
                        }}</For>
                    </div>
                    <Pagination page={Math.min(page(), totalPages() - 1)} totalPages={totalPages()} onChange={setPage} size="sm" />
                </Show>
            </Show>
        </div>
    );
}
